import React from 'react';

/**
 * Sample values used to render a human-readable preview of a template body.
 * Any placeholder missing here falls back to its raw `{token}` form.
 */
const PLACEHOLDER_SAMPLES: Record<string, string> = {
  memberName: 'Jane Doe',
  submitterName: 'Jane Doe',
  years: '3',
  yearSuffix: 's',
  invoiceMonth: '2026-07',
  invoicesUrl: 'https://app.example.com/invoices',
  anniversaryDate: 'August 1, 2026',
  days: '30',
};

const PLACEHOLDER_PATTERN = /\{(\w+)\}/g;

export const renderTemplatePreview = (
  body: string,
  overrides: Record<string, string> = {}
): string =>
  body.replace(
    PLACEHOLDER_PATTERN,
    (match, token) => overrides[token] ?? PLACEHOLDER_SAMPLES[token] ?? match
  );

/**
 * Matches the subset of inline Slack mrkdwn we render in the preview: *bold*,
 * _italic_, ~strike~ and <url|label> links.
 */
const SLACK_INLINE_PATTERN = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|<[^>\n]+>)/g;

const BLOCKQUOTE_LINE = /^>\s?(.*)$/;
const UNORDERED_LINE = /^[-•]\s+(.*)$/;
const ORDERED_LINE = /^\d+\.\s+(.*)$/;

/** Renders a single line's inline Slack formatting (bold/italic/strike/links). */
const renderInlineMrkdwn = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const pattern = new RegExp(SLACK_INLINE_PATTERN);
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const inner = token.slice(1, -1);
    if (token.startsWith('*')) {
      nodes.push(<strong key={key++}>{inner}</strong>);
    } else if (token.startsWith('_')) {
      nodes.push(<em key={key++}>{inner}</em>);
    } else if (token.startsWith('~')) {
      nodes.push(<s key={key++}>{inner}</s>);
    } else {
      const [url, label] = inner.split('|');
      nodes.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-prosigliere-blue underline"
        >
          {label ?? url}
        </a>
      );
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

/** Joins lines with `<br />` while applying inline formatting to each. */
const renderLinesWithBreaks = (lines: string[]): React.ReactNode[] =>
  lines.map((line, idx) => (
    <React.Fragment key={idx}>
      {idx > 0 && <br />}
      {renderInlineMrkdwn(line)}
    </React.Fragment>
  ));

/**
 * Renders a substituted preview string as React nodes with Slack-style
 * formatting: inline styles, line breaks, bullet/numbered lists and block
 * quotes. Mirrors https://docs.slack.dev/messaging/formatting-message-text/.
 */
export const renderSlackMrkdwn = (text: string): React.ReactNode[] => {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (BLOCKQUOTE_LINE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && BLOCKQUOTE_LINE.test(lines[i])) {
        quoted.push(lines[i].replace(BLOCKQUOTE_LINE, '$1'));
        i++;
      }
      blocks.push(
        <blockquote
          key={key++}
          className="border-l-4 border-prosigliere-grey pl-3 my-1 text-muted-foreground"
        >
          {renderLinesWithBreaks(quoted)}
        </blockquote>
      );
      continue;
    }

    const ordered = ORDERED_LINE.test(line);
    const unordered = UNORDERED_LINE.test(line);
    if (ordered || unordered) {
      const re = ordered ? ORDERED_LINE : UNORDERED_LINE;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i])) {
        items.push(lines[i].replace(re, '$1'));
        i++;
      }
      const listItems = items.map((item, idx) => <li key={idx}>{renderInlineMrkdwn(item)}</li>);
      blocks.push(
        ordered ? (
          <ol key={key++} className="list-decimal pl-6 my-1">
            {listItems}
          </ol>
        ) : (
          <ul key={key++} className="list-disc pl-6 my-1">
            {listItems}
          </ul>
        )
      );
      continue;
    }

    const plain: string[] = [];
    while (
      i < lines.length &&
      !BLOCKQUOTE_LINE.test(lines[i]) &&
      !ORDERED_LINE.test(lines[i]) &&
      !UNORDERED_LINE.test(lines[i])
    ) {
      plain.push(lines[i]);
      i++;
    }
    blocks.push(<React.Fragment key={key++}>{renderLinesWithBreaks(plain)}</React.Fragment>);
  }

  return blocks;
};

/**
 * Returns the list of placeholder tokens used in a body that are NOT part of
 * the allowed set for the template. Used to surface a clear validation error.
 */
export const findInvalidPlaceholders = (body: string, allowedParams: string[]): string[] => {
  const invalid = new Set<string>();
  let match: RegExpExecArray | null;
  const pattern = new RegExp(PLACEHOLDER_PATTERN);
  while ((match = pattern.exec(body)) !== null) {
    if (!allowedParams.includes(match[1])) {
      invalid.add(match[1]);
    }
  }
  return Array.from(invalid);
};
